import { ReactNode, createContext, useContext } from 'react'
import { z } from 'zod'
import { Form } from '$components'
import { UpdateMemberFormControls } from './controls'
import { UpdateMemberFormContent } from './content'

export enum Input {
  FirstName = 'first_name',
  LastName = 'last_name',
  Role = 'role',
}

const updateMemberSchema = z.object({
  [Input.FirstName]: z.string().min(1, { message: 'First name is required.' }),
  [Input.LastName]: z.string().min(1, { message: 'Last name is required.' }),
  [Input.Role]: z.object({
    id: z.string(),
    value: z.string(),
  }),
})

export type UpdateMemberSchema = z.infer<typeof updateMemberSchema>

type UpdateMemberFormContextType = {
  onSubmit: (values: UpdateMemberSchema) => void
  onCancel: () => void
  isLoading?: boolean
  defaultValues?: UpdateMemberSchema
}

const UpdateMemberFormContext = createContext<UpdateMemberFormContextType | undefined>(undefined)

type UpdateMemberFormProps = UpdateMemberFormContextType & {
  children?: ReactNode,
  defaultValues?: UpdateMemberSchema
}

export function UpdateMemberForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
  defaultValues,
}: UpdateMemberFormProps) {
  const context: UpdateMemberFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
    defaultValues,
  }

  return (
    <UpdateMemberFormContext.Provider value={context}>
      <Form<UpdateMemberSchema, typeof updateMemberSchema>
        schema={updateMemberSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {children}
      </Form>
    </UpdateMemberFormContext.Provider>
  )
}

UpdateMemberForm.Content = UpdateMemberFormContent
UpdateMemberForm.Controls = UpdateMemberFormControls

export function useUpdateMemberForm() {
  const context = useContext(UpdateMemberFormContext)

  if (context === undefined) {
    throw new Error('useUpdateMemberForm must be used within an UpdateMemberFormProvider')
  }

  return context
}
